import { useCallback, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { listEmpresas, type Empresa } from '@/services/api/empresas';
import { LoadingScreen } from '@/components/common/LoadingScreen';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { routes } from '@/lib/routes';
import { EmpresaEditDialog } from '@/modules/empresas/components/EmpresaEditDialog';
import { GlobalSearch } from '@/components/common/GlobalSearch';
import { AuditLogDrawer } from '@/components/common/AuditLogDrawer';
import { MobileWelcomeWizard } from '@/components/common/MobileWelcomeWizard';

const SIDEBAR_COLLAPSED_KEY = 'manaxer.sidebar.collapsed';

/**
 * Layout principal. Requiere URL con `:empresaId`.
 */
export function AppLayout() {
  const { empresaId } = useParams<{ empresaId: string }>();
  const { isApex, isPartner, profile } = useAuth();
  const [empresas, setEmpresas] = useState<Empresa[] | null>(null);
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    editing: Empresa | null;
  }>({ open: false, editing: null });
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  // TODO-MDC-20 PWA-2: drawer del sidebar en mobile (<md).
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // TODO-MDC-23: cerrar drawer mobile al cambiar de ruta. Sin esto el
  // sidebar quedaba "clavado" cuando el usuario tocaba un item.
  const location = useLocation();
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0');
  }, [sidebarCollapsed]);

  // Global Cmd+K / Ctrl+K → abre el buscador. Funciona desde cualquier
  // parte de la app, salvo si hay un input/textarea ya recibiendo Cmd+K
  // (en los browsers modernos cmd+K está libre para apps).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const reloadEmpresas = useCallback(async () => {
    const res = await listEmpresas();
    setEmpresas(res.ok ? res.data : []);
  }, []);

  useEffect(() => {
    reloadEmpresas();
  }, [reloadEmpresas]);

  if (!empresaId) return <Navigate to="/" replace />;
  if (!empresas) return <LoadingScreen message="Cargando empresas…" />;

  const empresaActiva = empresas.find((e) => e.id === empresaId);

  if (!empresaActiva) {
    if (isApex) return <Navigate to={routes.selectEmpresa} replace />;
    if (profile?.empresa_id)
      return <Navigate to={routes.empresa.dashboard(profile.empresa_id)} replace />;
    return <Navigate to="/login" replace />;
  }

  if (!isApex && profile?.empresa_id && profile.empresa_id !== empresaId) {
    return <Navigate to={routes.empresa.dashboard(profile.empresa_id)} replace />;
  }

  const canEdit = isApex || isPartner;
  const canCreate = isApex;

  function openEdit() {
    setEditDialog({ open: true, editing: empresaActiva ?? null });
  }
  function openNew() {
    setEditDialog({ open: true, editing: null });
  }

  return (
    <div className="flex min-h-screen">
      {/* Sidebar desktop (md+) — visible siempre */}
      <div className="hidden md:flex">
        <Sidebar
          empresaActiva={empresaActiva}
          empresas={empresas}
          canEdit={canEdit}
          canCreate={canCreate}
          onEdit={openEdit}
          onCreate={openNew}
          collapsed={sidebarCollapsed}
          onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
        />
      </div>

      {/* TODO-MDC-20 PWA-2: Sidebar mobile como drawer overlay (<md) */}
      {mobileNavOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] md:hidden"
            onClick={() => setMobileNavOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 flex md:hidden">
            <Sidebar
              empresaActiva={empresaActiva}
              empresas={empresas}
              canEdit={canEdit}
              canCreate={canCreate}
              onEdit={() => {
                setMobileNavOpen(false);
                openEdit();
              }}
              onCreate={() => {
                setMobileNavOpen(false);
                openNew();
              }}
              collapsed={false}
              onToggleCollapsed={() => setMobileNavOpen(false)}
            />
            <button
              type="button"
              onClick={() => setMobileNavOpen(false)}
              className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-white/90 text-neutral-muted shadow-premium-soft hover:text-neutral-ink"
              aria-label="Cerrar menú"
            >
              <X size={16} />
            </button>
          </div>
        </>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          empresaActiva={empresaActiva}
          onOpenSearch={() => setSearchOpen(true)}
          onOpenAudit={isApex || isPartner ? () => setAuditOpen(true) : undefined}
          onOpenMobileNav={() => setMobileNavOpen(true)}
        />
        <main className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 md:px-8 md:py-6">
          <Outlet context={{ empresaActiva }} />
        </main>
      </div>

      <EmpresaEditDialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, editing: null })}
        editing={editDialog.editing}
        onSaved={() => {
          reloadEmpresas();
        }}
      />

      <GlobalSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        empresaId={empresaActiva.id}
      />

      {(isApex || isPartner) && (
        <AuditLogDrawer
          open={auditOpen}
          onClose={() => setAuditOpen(false)}
          empresaId={empresaActiva.id}
        />
      )}

      {/* TODO-MDC-21: bienvenida móvil primera vez */}
      <MobileWelcomeWizard empresaId={empresaActiva.id} />
    </div>
  );
}
